/**
 * useOnboardingOtpStart — Pillar 3 typed mutation hook for the
 * pre-auth onboarding OTP dispatch surface.
 *
 * Wire contract: `POST /v1/auth/onboarding-otp-start` — GENERATED
 * `OnboardingOtpStart{Request,Response}` (`@appic/lib/wire/generated`).
 *
 * Closes BUG-FE-ONBOARDING-OTP-WRONG-API-SMS-NOT-TOTP-2026-05-24 (P0).
 * Sibling: BUG-PORTAL-OTP-SUPABASE-SMS-INSTEAD-OF-TOTP-VIA-COMMS (P1).
 *
 * Why this hook exists:
 *   - The previous FE called `supabase.auth.signInWithOtp({ phone })`
 *     directly. The Supabase project has no sms_provider configured →
 *     400 "Unsupported phone provider". Even if a provider were added
 *     on the Supabase side, it would bypass the comms service that
 *     already owns the Twilio + WhatsApp send path (per
 *     COMMS-CANONICAL-V1) and would diverge from the rest of the CLANDA
 *     architecture (SL-mediated mutations only).
 *   - Service-layer mints the code, persists a challenge, and asks
 *     comms to dispatch via Twilio / WhatsApp. Verification (see
 *     `useOnboardingOtpVerify`) returns a Supabase session that the FE
 *     installs on the existing `sb-clanda-onboarding` client.
 *
 * Pre-auth call: no Supabase JWT cookie is present yet. The SL
 * onboarding-otp routes are intentionally unauthenticated (rate-limited
 * 3/15min per phone hash) so this hook does NOT inject `Authorization`
 * — `serviceFetch` already degrades gracefully when no token is
 * available.
 */

import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'
import { mutateTyped } from '@appic/lib/http/mutateTyped'
import { SLApiError } from '@appic/lib/http/slErrors'
import {
  OnboardingOtpStartRequest as OnboardingOtpStartRequestSchema,
  OnboardingOtpStartResponse as OnboardingOtpStartResponseSchema,
} from '@appic/lib/wire/generated/schemas.generated'
import type {
  OnboardingOtpStartRequest,
  OnboardingOtpStartResponse,
} from '@appic/lib/wire/generated/types.generated'

/**
 * FE-owned per-surface error taxonomy for the onboarding-otp surfaces
 * (start + verify share it). Mirrors the SL handler's `error_code` values
 * for typed consumer branching. This is NOT part of the codegen wire
 * contract — the SL openapi models the error envelope generically — so it
 * stays a hand-maintained enum next to the hooks that branch on it
 * (KPI #2: only the request/response CONTRACT is generated; the FE error
 * taxonomy is a local rendering concern).
 */
export const OnboardingOtpErrorCodeSchema = z.enum([
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_ERROR',
  'RATE_LIMITED',
  'INTERNAL',
  'TIMEOUT',
  'CONFLICT',
  'INVALID_PHONE',
  'COMMS_DISPATCH_FAILED',
  'CHALLENGE_NOT_FOUND',
  'CHALLENGE_EXPIRED',
  'CODE_INVALID',
  'TOO_MANY_ATTEMPTS',
])
export type OnboardingOtpErrorCode = z.infer<typeof OnboardingOtpErrorCodeSchema>

const SLUG = 'auth.onboarding.otp.start'
const PATH = '/v1/auth/onboarding-otp-start'

/**
 * Onboarding-OTP-specific error class. Mirrors the per-surface error
 * code enum from the wire envelope so consumer screens can branch on
 * `instanceof OnboardingOtpError` + `.error_code` without importing
 * `SLApiError` directly.
 */
export class OnboardingOtpError extends Error {
  readonly error_code: OnboardingOtpErrorCode
  constructor(code: OnboardingOtpErrorCode, message?: string) {
    super(message ?? code)
    this.name = 'OnboardingOtpError'
    this.error_code = code
  }
}

/**
 * React-query mutation that POSTs the phone number to SL and returns
 * the `{challenge_id, expires_at}` envelope. On HTTP non-2xx the
 * underlying `SLApiError` is re-thrown as `OnboardingOtpError` carrying
 * the typed `error_code` from `OnboardingOtpErrorCodeSchema`.
 *
 * On response shape drift `mutateTyped` throws `ContractMismatchError`
 * (D-33 R1, never silently degraded) and the mutation propagates that
 * unchanged — the consumer screen renders a generic error banner.
 */
export function useOnboardingOtpStart() {
  return useMutation<OnboardingOtpStartResponse, Error, OnboardingOtpStartRequest>({
    mutationFn: async (input) => {
      try {
        return await mutateTyped({
          method: 'POST',
          path: PATH,
          body: input,
          requestSchema: OnboardingOtpStartRequestSchema,
          responseSchema: OnboardingOtpStartResponseSchema,
          slug: SLUG,
        })
      } catch (e) {
        if (e instanceof SLApiError) {
          // Validate the wire `code` against the typed enum before
          // constructing OnboardingOtpError — `e.code` is the raw string
          // from the SL envelope; if SL ships a code not in the FE enum
          // (transport fallbacks, new producer codes), `as` would smuggle
          // an invalid literal through the type system and break enum-
          // based i18n / branching downstream. Fall back to INTERNAL so
          // consumers always see a stable, render-safe code (Codex P2 on
          // PR #1751, 2026-05-25).
          const parsed = OnboardingOtpErrorCodeSchema.safeParse(e.code)
          throw new OnboardingOtpError(
            parsed.success ? parsed.data : 'INTERNAL',
            e.message,
          )
        }
        throw e
      }
    },
  })
}
